import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2, Sparkles, Search, FileText, ShipWheel, Workflow, Trash2, BotMessageSquare, X, CheckCircle,
  ChevronDown, FlaskConical,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAssistantWorkflowActions } from '../hooks/useAssistantWorkflowActions';
import { useTheme } from '@/components/theme-provider';
import { useSidebar } from '@/hooks/useSidebar';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import { ModeToggle } from '@/components/mode-toggle';
import { apiClient, type SuggestedWorkflow } from '../api/client';
import { WorkflowSuggestionsChat } from '@/components/WorkflowSuggestionsChat';
import { SimulatedSuggestionsModal } from '@/components/workflows/SimulatedSuggestionsModal';
import { hasRealIntegration } from '@/lib/integrationStatus';

import slackColorIcon from '../assets/icons/slack-color.png';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import { ReactComponent as GCPIcon } from '../assets/icons/gcp.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';
import datadogIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import cloudflareIcon from '../assets/icons/cloudflare.svg';

import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';

interface RunbookUpload {
  id: string;
  filename: string;
  source: string;
  analysis_status: string;
  automated_workflow_ids: string[];
  created_at: string;
}

const SOURCE_CONFIG: Record<string, { label: string; order: number }> = {
  kubernetes: { label: 'Kubernetes', order: 0 },
  aws: { label: 'AWS', order: 1 },
  github: { label: 'GitHub', order: 2 },
  pagerduty: { label: 'PagerDuty', order: 3 },
  datadog: { label: 'Datadog', order: 4 },
  cloudflare: { label: 'Cloudflare', order: 5 },
  posthog: { label: 'PostHog', order: 6 },
  vercel: { label: 'Vercel', order: 7 },
  railway: { label: 'Railway', order: 8 },
  flyio: { label: 'Fly.io', order: 9 },
  nebius: { label: 'Nebius', order: 10 },
  beam: { label: 'Beam', order: 11 },
  daytona: { label: 'Daytona', order: 12 },
  supabase: { label: 'Supabase', order: 13 },
  planetscale: { label: 'PlanetScale', order: 14 },
  neon: { label: 'Neon', order: 15 },
  clickhouse: { label: 'ClickHouse', order: 16 },
  jenkins: { label: 'Jenkins', order: 17 },
  circleci: { label: 'CircleCI', order: 18 },
  sonarcloud: { label: 'SonarCloud', order: 18.5 },
  okta: { label: 'Okta', order: 18.6 },
  databricks: { label: 'Databricks', order: 18.65 },
  gcp: { label: 'Google Cloud', order: 18.66 },
  kyverno: { label: 'Kyverno', order: 18.7 },
  trivy: { label: 'Trivy', order: 18.8 },
  terraform: { label: 'Terraform Cloud', order: 19 },
  pulumi: { label: 'Pulumi Cloud', order: 20 },
  vault: { label: 'HashiCorp Vault', order: 21 },
  infisical: { label: 'Infisical', order: 22 },
  slack: { label: 'Slack', order: 23 },
  request: { label: 'Kestrel', order: 24 },
  chat: { label: 'Chat', order: 25 },
};

function SourceIcon({ source, isDark, size = 16 }: { source: string; isDark: boolean; size?: number }) {
  const s = { width: size, height: size };
  const cls = 'rounded';
  switch (source) {
    case 'kubernetes':
      return <ShipWheel style={s} className="text-blue-500" />;
    case 'github':
      return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" style={s} className={cls} />;
    case 'slack':
      return <img src={slackColorIcon} alt="Slack" style={s} className={cls} />;
    case 'pagerduty':
      return <img src={pagerdutyColorIcon} alt="PagerDuty" style={s} className={cls} />;
    case 'aws':
    case 'aws-cost':
    case 'aws-ops':
    case 'aws-security':
    case 'aws-data':
      return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" style={s} className={cls} />;
    case 'posthog':
      return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" style={s} className={`${cls} object-contain`} />;
    case 'vercel':
      return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'railway':
      return <img src={railwayIcon} alt="Railway" style={{ width: size * 0.8, height: size * 0.8, filter: isDark ? 'invert(1)' : 'none' }} className={`${cls} object-contain`} />;
    case 'flyio':
      return <img src={flyioIcon} alt="Fly.io" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'nebius':
      return <img src={nebiusIcon} alt="Nebius" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'beam':
      return <BeamIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'daytona':
      return <DaytonaIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'supabase':
      return <img src={supabaseIcon} alt="Supabase" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'planetscale':
      return <PlanetScaleIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'neon':
      return <img src={neonIcon} alt="Neon" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'clickhouse':
      return <img src={clickhouseIcon} alt="ClickHouse" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'jenkins':
      return <img src={jenkinsIcon} alt="Jenkins" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'circleci':
      return <CircleCIIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'sonarcloud':
      return <SonarCloudIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'okta':
      return <OktaIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'databricks':
      return <img src={databricksIcon} alt="Databricks" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'gcp':
    case 'gcp-cost':
    case 'gcp-security':
    case 'gcp-data':
      return <GCPIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'kyverno':
      return <img src={kyvernoIcon} alt="Kyverno" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'trivy':
      return <img src={trivyIcon} alt="Trivy" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'terraform':
      return <img src={terraformIcon} alt="Terraform" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'pulumi':
      return <img src={pulumiIcon} alt="Pulumi" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'vault':
      return <img src={vaultIcon} alt="Vault" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'infisical':
      return <img src={infisicalIcon} alt="Infisical" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'datadog':
      return <img src={isDark ? datadogDarkIcon : datadogIcon} alt="Datadog" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'cloudflare':
      return <img src={cloudflareIcon} alt="Cloudflare" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'request':
      return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" style={s} className={cls} />;
    case 'chat':
      return <BotMessageSquare style={{ width: size * 0.85, height: size * 0.85 }} className={isDark ? 'text-white' : 'text-black'} />;
    default:
      return <Workflow style={{ width: size * 0.85, height: size * 0.85 }} className={isDark ? 'text-white' : 'text-black'} />;
  }
}

const WorkflowSuggestions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  const { isMobile } = useSidebar();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const [suggestions, setSuggestions] = useState<SuggestedWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSource, setActiveSource] = useState('all');
  const [runbooks, setRunbooks] = useState<RunbookUpload[]>([]);
  const [viewingRunbook, setViewingRunbook] = useState<{ name: string; content: string } | null>(null);
  // Simulated chat: with nothing real connected, the assistant treats every
  // integration as connected and creates sandboxed (simulated) workflows.
  const [noRealIntegrations, setNoRealIntegrations] = useState(false);
  const { saveSuggestion, createSuggestion, renameDialogElement } = useAssistantWorkflowActions({ onSaved: () => loadData(), simulated: noRealIntegrations });
  const [chatVisible, setChatVisible] = useState(() => !window.matchMedia('(max-width: 767px)').matches);
  // Simulated-suggestion picker: auto-opens when nothing is connected (there is
  // no basis for real suggestions), and is reachable any time from the caret so
  // connected customers can explore other integrations too.
  const [simulatedPickerOpen, setSimulatedPickerOpen] = useState(false);
  const [chatPanelWidth, setChatPanelWidth] = useState(() => Math.round(window.innerWidth * 0.28));
  const isResizingChat = useRef(false);
  const resizeChatLineRef = useRef<HTMLDivElement>(null);

  // Act on navigation state from elsewhere in the product: the onboarding
  // wizard asks for generation outright, and the builder's "not sure what to
  // automate" link asks for the simulated picker when nothing is connected.
  useEffect(() => {
    const state = location.state as { triggerGeneration?: boolean; openSimulatedPicker?: boolean } | null;
    if (!state?.triggerGeneration && !state?.openSimulatedPicker) return;
    if (state.triggerGeneration && !suggestLoading) {
      handleSuggest();
    } else if (state.openSimulatedPicker) {
      setSimulatedPickerOpen(true);
    }
    // Clear the state so it doesn't re-trigger on back/forward.
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const startChatResize = useCallback(() => {
    isResizingChat.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (resizeChatLineRef.current) {
      resizeChatLineRef.current.style.transitionDelay = '0ms';
      resizeChatLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.5)';
    }
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isResizingChat.current) {
        const newWidth = Math.max(Math.round(window.innerWidth * 0.23), Math.min(Math.round(window.innerWidth * 0.35), window.innerWidth - e.clientX));
        setChatPanelWidth(newWidth);
      }
    };
    const onMouseUp = () => {
      if (isResizingChat.current) {
        isResizingChat.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (resizeChatLineRef.current) {
          resizeChatLineRef.current.style.transitionDelay = '0ms';
          resizeChatLineRef.current.style.backgroundColor = 'transparent';
        }
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (!authLoading && user) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sugg, rb, integrations] = await Promise.all([
        apiClient.getSuggestedWorkflows().catch(() => []),
        apiClient.getRunbookUploads().catch(() => []),
        apiClient.getWorkflowIntegrationsStatus().catch(() => []),
      ]);
      const real = (sugg || []).filter((sw: SuggestedWorkflow) => sw.name !== '__generating__');
      const isGenerating = (sugg || []).some((sw: SuggestedWorkflow) => sw.name === '__generating__');
      setSuggestions(real);
      setRunbooks(rb || []);
      setNoRealIntegrations(!hasRealIntegration(integrations));

      if (isGenerating) {
        setSuggestLoading(true);
        startPolling();
      } else if (!hasRealIntegration(integrations) && real.length === 0) {
        // Nothing connected and nothing to show: offer the simulated picker
        // instead of an empty page the customer cannot act on.
        setSimulatedPickerOpen(true);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    let attempts = 0;
    const maxAttempts = 90;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const sugg = await apiClient.getSuggestedWorkflows();
        const real = (sugg || []).filter((sw: SuggestedWorkflow) => sw.name !== '__generating__');
        const stillGenerating = (sugg || []).some((sw: SuggestedWorkflow) => sw.name === '__generating__');
        if (real.length > 0) setSuggestions(real);
        if (!stillGenerating || attempts >= maxAttempts) {
          setSuggestLoading(false);
          clearInterval(poll);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  const handleSuggest = async (simulatedSources?: string[]) => {
    setSuggestLoading(true);
    setError(null);
    try {
      const resp = await apiClient.suggestWorkflows(simulatedSources);
      if ((resp as any)?.status === 'no_integrations') {
        // Nothing connected: the simulated picker is the way forward.
        setSuggestLoading(false);
        setSimulatedPickerOpen(true);
        return;
      }
      if ((resp as any)?.success === false) {
        setSuggestLoading(false);
        setError((resp as any)?.error || 'Failed to generate suggestions');
        return;
      }
      startPolling();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to generate suggestions');
      setSuggestLoading(false);
    }
  };

  const handleUseSuggestion = (suggestion: SuggestedWorkflow) => {
    // Simulated suggestions must open the builder in simulated mode: their
    // integrations aren't connected, so the blocks would otherwise be
    // undraggable and the workflow unbuildable.
    navigate(suggestion.simulated ? '/workflows/new?simulated=1' : '/workflows/new', {
      state: {
        suggestion: {
          name: suggestion.name,
          description: suggestion.description,
          definition: suggestion.definition,
          trigger_config: suggestion.trigger_config,
          nl_prompt: suggestion.nl_prompt || '',
          from_suggestion_id: suggestion.id,
        },
      },
    });
  };

  const filteredSuggestions = useMemo(() => {
    let filtered = suggestions;
    if (activeSource === 'created') {
      filtered = filtered.filter(s => s.status === 'created');
    } else if (activeSource === 'simulated') {
      filtered = filtered.filter(s => s.simulated);
    } else if (activeSource !== 'all') {
      filtered = filtered.filter(s => (s.source || 'kubernetes') === activeSource);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [suggestions, activeSource, searchQuery]);

  const groupedSuggestions = useMemo(() => {
    const grouped: Record<string, SuggestedWorkflow[]> = {};
    for (const s of filteredSuggestions) {
      const src = s.source || 'kubernetes';
      if (!grouped[src]) grouped[src] = [];
      grouped[src].push(s);
    }
    return Object.entries(grouped).sort(([a], [b]) => {
      const aOrder = SOURCE_CONFIG[a]?.order ?? 99;
      const bOrder = SOURCE_CONFIG[b]?.order ?? 99;
      return aOrder - bOrder;
    });
  }, [filteredSuggestions]);

  const simulatedCount = useMemo(() => suggestions.filter(s => s.simulated).length, [suggestions]);

  const availableSources = useMemo(() => {
    const sources = new Set<string>();
    for (const s of suggestions) sources.add(s.source || 'kubernetes');
    return Array.from(sources).sort((a, b) => {
      const aOrder = SOURCE_CONFIG[a]?.order ?? 99;
      const bOrder = SOURCE_CONFIG[b]?.order ?? 99;
      return aOrder - bOrder;
    });
  }, [suggestions]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left panel — Workflow Library */}
      <div className="min-w-0 flex-1 flex flex-col border-r overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8 py-4 border-b shrink-0">
          <div className="flex items-start justify-between mb-3 gap-2">
            <div className="flex items-start gap-1 min-w-0">
              <MobileMenuButton />
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-foreground">Workflow Library</h1>
                <p className="text-muted-foreground mt-1 text-sm leading-relaxed">AI-suggested workflows based on your integrations and runbooks.</p>
              </div>
            </div>
            <div className="mt-1 shrink-0"><ModeToggle /></div>
          </div>
          {error && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search workflows..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex-1" />
            <div className="flex items-center shrink-0">
              <button
                onClick={() => handleSuggest()}
                disabled={suggestLoading}
                className="suggest-btn relative inline-flex items-center gap-2 px-4 py-2 h-9 text-sm font-medium rounded-md text-foreground disabled:opacity-50"
              >
                <span className="suggest-btn-border" />
                <span className="suggest-btn-bg" />
                <span className="relative z-10 flex items-center gap-2">
                  {suggestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {suggestLoading ? 'Generating...' : 'Suggest Workflows'}
                </span>
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSimulatedPickerOpen(true)}
                disabled={suggestLoading}
                className="h-9 w-8 p-0 ml-1"
                title="Generate simulated suggestions for integrations you haven't connected"
                aria-label="Generate simulated suggestions"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            </div>
            {!chatVisible && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatVisible(true)}
                className="h-9 gap-2 shrink-0"
              >
                Open Chat
              </Button>
            )}
          </div>
        </div>

        {/* Source tabs + grid */}
        <div className="flex-1 overflow-y-auto p-6 pt-4">
          <Tabs value={activeSource} onValueChange={setActiveSource}>
            <TabsList className="mb-4 flex-wrap h-auto gap-1 bg-transparent border border-border rounded-lg p-1">
              <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
              {availableSources.map(src => (
                <TabsTrigger key={src} value={src} className="text-xs gap-1.5">
                  <SourceIcon source={src} isDark={isDark} size={12} />
                  {SOURCE_CONFIG[src]?.label || src}
                </TabsTrigger>
              ))}
              <TabsTrigger value="uploads" className="text-xs gap-1.5">
                <FileText className="w-3 h-3 text-foreground" />
                Uploads
                {runbooks.length > 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1 ml-0.5">{runbooks.length}</Badge>
                )}
              </TabsTrigger>
              {simulatedCount > 0 && (
                <TabsTrigger value="simulated" className="text-xs gap-1.5">
                  <FlaskConical className="w-3 h-3 text-foreground" />
                  Simulated
                  <Badge variant="secondary" className="text-[10px] h-4 px-1 ml-0.5">{simulatedCount}</Badge>
                </TabsTrigger>
              )}
              <TabsTrigger value="created" className="text-xs gap-1.5">
                <CheckCircle className="w-3 h-3 text-green-600" />
                Created
                {suggestions.filter(s => s.status === 'created').length > 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1 ml-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">{suggestions.filter(s => s.status === 'created').length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeSource} className="mt-0">
              {activeSource === 'uploads' ? (
                runbooks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <p className="text-sm text-muted-foreground">No uploaded runbooks yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Upload runbooks or scripts via the chat assistant to see them here.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {runbooks.map(rb => (
                      <Card key={rb.id} className="group">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="shrink-0">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1 flex items-center gap-2">
                              <h4 className="text-sm font-medium truncate">{rb.filename}</h4>
                              <RunbookStatus status={rb.analysis_status} workflowIds={rb.automated_workflow_ids} />
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                title="View file"
                                onClick={async () => {
                                  try {
                                    const content = await apiClient.getRunbookContent(rb.id);
                                    setViewingRunbook({ name: rb.filename, content });
                                  } catch {}
                                }}
                              >
                                <Search className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                onClick={async () => {
                                  try {
                                    await apiClient.deleteRunbook(rb.id);
                                    setRunbooks(prev => prev.filter(r => r.id !== rb.id));
                                  } catch {}
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )
              ) : filteredSuggestions.length === 0 ? (
                <EmptyState
                  hasAnySuggestions={suggestions.length > 0}
                  isGenerating={suggestLoading}
                  onSuggest={handleSuggest}
                />
              ) : (
                <div className="space-y-6">
                  {groupedSuggestions.map(([source, items]) => (
                    <div key={source}>
                      {activeSource === 'all' && (
                        <div className="flex items-center gap-2 mb-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          <SourceIcon source={source} isDark={isDark} size={14} />
                          {SOURCE_CONFIG[source]?.label || source}
                          <Badge variant="secondary" className="text-xs ml-1">{items.length}</Badge>
                        </div>
                      )}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {items.map((suggestion, i) => (
                          <SuggestionCard
                            key={`${source}-${i}`}
                            suggestion={suggestion}
                            isDark={isDark}
                            onUse={handleUseSuggestion}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right panel — Interactive Chat (always mounted, hidden via CSS to preserve state) */}
      <div className={chatVisible ? "contents" : "hidden"}>
        <div
          onMouseDown={(e) => { e.preventDefault(); startChatResize(); }}
          onMouseEnter={() => {
            if (!isResizingChat.current && resizeChatLineRef.current) {
              resizeChatLineRef.current.style.transitionDelay = '300ms';
              resizeChatLineRef.current.style.backgroundColor = 'hsl(var(--border))';
            }
          }}
          onMouseLeave={() => {
            if (!isResizingChat.current && resizeChatLineRef.current) {
              resizeChatLineRef.current.style.transitionDelay = '0ms';
              resizeChatLineRef.current.style.backgroundColor = 'transparent';
            }
          }}
          className={isMobile ? 'hidden' : 'shrink-0 cursor-col-resize z-20 flex items-stretch justify-center'}
          style={{ width: '9px', marginLeft: '-4px', marginRight: '-5px' }}
        >
          <div
            ref={resizeChatLineRef}
            className="w-[3px] rounded-full bg-transparent transition-colors"
            style={{ transitionDuration: '150ms', transitionDelay: '0ms', pointerEvents: 'none' }}
          />
        </div>
        <div
          className={isMobile
            ? 'fixed inset-0 z-50 flex flex-col bg-background overflow-hidden'
            : 'min-w-0 flex flex-col border-l border-border overflow-hidden'}
          style={isMobile ? undefined : { width: chatPanelWidth }}
        >
          <WorkflowSuggestionsChat
            onClose={() => setChatVisible(false)}
            simulated={noRealIntegrations}
            onRunbookUploaded={() => {
              apiClient.getRunbookUploads().then(rb => setRunbooks(rb || [])).catch(() => {});
            }}
            onSuggestionSave={saveSuggestion}
            onSuggestionCreate={createSuggestion}
          />
        </div>
      </div>

      {/* Rename workflow dialog (on 409 conflict) */}
      {renameDialogElement}

      {/* Runbook content viewer */}
      {viewingRunbook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setViewingRunbook(null)}>
          <div className="bg-background border rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col m-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-medium truncate">{viewingRunbook.name}</h3>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewingRunbook(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <pre className="text-xs font-mono whitespace-pre-wrap text-foreground leading-relaxed">{viewingRunbook.content}</pre>
            </div>
          </div>
        </div>
      )}

      <SimulatedSuggestionsModal
        open={simulatedPickerOpen}
        onClose={() => setSimulatedPickerOpen(false)}
        onGenerate={sources => handleSuggest(sources)}
      />
    </div>
  );
};

function SuggestionCard({
  suggestion,
  isDark,
  onUse,
}: {
  suggestion: SuggestedWorkflow;
  isDark: boolean;
  onUse: (s: SuggestedWorkflow) => void;
}) {
  const source = suggestion.source || 'kubernetes';
  const stepCount = suggestion.definition?.nodes?.length || 0;

  return (
    <Card
      className="hover:bg-accent/50 transition-colors cursor-pointer group"
      onClick={() => onUse(suggestion)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0">
            <SourceIcon source={source} isDark={isDark} size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <h4 className="text-sm font-medium leading-tight truncate">{suggestion.name}</h4>
                {suggestion.status === 'created' && (
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] h-4 px-1.5 shrink-0">Created</Badge>
                )}
                {suggestion.simulated && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] h-4 px-1.5 shrink-0"
                    title="Generated for an integration you haven't connected. Opens in simulated mode."
                  >
                    Simulated
                  </Badge>
                )}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{stepCount} steps</span>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {suggestion.description}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({
  hasAnySuggestions,
  isGenerating,
  onSuggest,
}: {
  hasAnySuggestions: boolean;
  isGenerating: boolean;
  onSuggest: () => void;
}) {
  if (isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-3" />
        <p className="text-sm font-medium">Generating workflow suggestions...</p>
        <p className="text-xs text-muted-foreground mt-1">
          Analyzing your integrations and runbooks or scripts
        </p>
      </div>
    );
  }

  if (hasAnySuggestions) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-muted-foreground">No workflows match your filters</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <h3 className="text-sm font-medium">No suggested workflows yet</h3>
      <p className="text-xs text-muted-foreground mt-1 max-w-sm">
        Generate AI-powered workflow suggestions based on your connected integrations and uploaded runbooks or scripts.
      </p>
      <Button onClick={onSuggest} variant="outline" className="mt-4 text-sm">
        Generate Suggestions
      </Button>
    </div>
  );
}

function RunbookStatus({ status, workflowIds }: { status: string; workflowIds?: string[] }) {
  switch (status) {
    case 'automated':
      if (workflowIds?.length) {
        return (
          <a href={`/workflows/${workflowIds[0]}`} className="no-underline" onClick={e => e.stopPropagation()}>
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal hover:bg-green-200 dark:hover:bg-green-900/50 cursor-pointer">Automated</Badge>
          </a>
        );
      }
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Automated</Badge>;
    case 'completed':
      return <Badge variant="secondary" className="text-xs font-normal">Analyzed</Badge>;
    case 'analyzing':
      return (
        <Badge variant="secondary" className="text-xs font-normal">
          <Loader2 className="w-3 h-3 animate-spin mr-1" /> Analyzing
        </Badge>
      );
    case 'failed':
      return <Badge variant="secondary" className="text-xs font-normal text-red-600 dark:text-red-400">Failed</Badge>;
    default:
      return <Badge variant="outline" className="text-xs font-normal">Pending</Badge>;
  }
}

export default WorkflowSuggestions;
